import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { VILLES } from 'app/classesTs/Ville';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import Swal from 'sweetalert2';
import { filter, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subscription, Subject } from 'rxjs';

interface AppState {
  page: number;
  ville: string;
  search: string;
  allData: any[];
  villeData: Map<string, any[]>;
  isInitialLoadComplete: boolean;
  isLoading: boolean;
}

@Component({
  selector: 'app-promoteur-list',
  templateUrl: './promoteur-list.component.html',
  styleUrls: ['./promoteur-list.component.scss']
})
export class PromoteurListComponent implements OnInit, OnDestroy {
  // Application state management
  private appState: AppState = {
    page: 1,
    ville: 'all',
    search: '',
    allData: [],
    villeData: new Map<string, any[]>(),
    isInitialLoadComplete: false,
    isLoading: false
  };

  // Display data - derived from state
  filteredAgencies: any[] = [];
  
  // UI State - synchronized with appState
  get selectedVille(): string { return this.appState.ville; }
  get searchTerm(): string { return this.appState.search; }
  get currentPage(): number { return this.appState.page; }
  get isLoading(): boolean { return this.appState.isLoading; }
  get hasData(): boolean { return this.appState.allData.length > 0; }
  
  // For template binding (two-way binding)
  set selectedVille(value: string) {
    this.appState.ville = value;
    this.appState.page = 1; // Reset to page 1 when changing ville
    this.appState.search = ''; // Clear search when changing ville
    this.updateDisplayFromCache();
    this.updateUrlState();
  }
  
  set searchTerm(value: string) {
    this.appState.search = value;
    this.onSearchInput();
  }
  
  set currentPage(value: number) {
    this.appState.page = value;
    this.updateUrlState();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  
  // Constants
  villes = VILLES;
  itemsPerPage: number = 10;
  totalItems: number = 0;
  
  // UI State
  errorMessage: string = '';
  isRefreshing: boolean = false;
  
  // Subscriptions
  private routerSubscription: Subscription;
  private searchSubject = new Subject<string>();

  constructor(
    private agenciesService: AgenciesService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private cdr: ChangeDetectorRef
  ) {
    // Subscribe to router events to detect when we return to this component
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Check if we're coming back to this component
        if (event.url.includes('/promoteur-list')) {
          this.handleRouteReturn();
        }
      });
  }

  ngOnInit(): void {
    // Setup search debounce
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.performSearch();
    });

    // Initial load - check URL params first, then load data
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.searchSubject.unsubscribe();
  }

  /**
   * Initialize component with proper state management
   */
  private initializeComponent(): void {
    // First, restore state from URL
    this.route.queryParams.subscribe(params => {
      this.restoreStateFromUrl(params);
      
      // Then load or display data based on state
      this.loadDataBasedOnState();
    });
  }

  /**
   * Restore state from URL parameters
   */
  private restoreStateFromUrl(params: any): void {
    const page = params['page'] ? Math.max(1, +params['page']) : 1;
    const ville = params['ville'] || 'all';
    const search = params['search'] || '';
    
    this.appState.page = page;
    this.appState.ville = ville;
    this.appState.search = search;
    
    console.log('Restored state from URL:', { page, ville, search });
  }

  /**
   * Load data based on current state
   */
  private loadDataBasedOnState(): void {
    // Check if we have data in memory
    if (this.appState.allData.length > 0) {
      // We have cached data, just update display
      this.updateDisplayFromCache();
      return;
    }
    
    // No cached data, load from API
    this.loadInitialDataFromApi();
  }

  /**
   * Load initial data from API
   */
  private loadInitialDataFromApi(): void {
    if (this.appState.isLoading) return;
    
    this.appState.isLoading = true;
    this.errorMessage = '';
    
    this.agenciesService.getListPromoteur().subscribe({
      next: (response: any) => {
        const processedData = this.extractDataFromResponse(response);
        
        // Store in memory cache (NO LOCALSTORAGE - only memory)
        this.appState.allData = processedData;
        this.appState.isInitialLoadComplete = true;
        this.appState.isLoading = false;
        
        // Update display
        this.updateDisplayFromCache();
        
        console.log('Initial data loaded from API:', processedData.length);
      },
      error: (error) => {
        console.error('Error loading initial data:', error);
        this.errorMessage = error.message || 'Erreur lors du chargement des données';
        this.appState.isLoading = false;
        this.filteredAgencies = [];
        this.totalItems = 0;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Update display from cached data
   */
  private updateDisplayFromCache(): void {
    try {
      let dataToDisplay: any[];
      
      if (this.appState.ville === 'all') {
        dataToDisplay = this.appState.allData;
      } else {
        // Get data for specific ville
        const villeData = this.getVilleData(this.appState.ville);
        dataToDisplay = villeData;
      }
      
      // Transform for display
      const transformedData = this.transformData(dataToDisplay);
      
      // Apply search filter if any
      if (this.appState.search) {
        const searchLower = this.appState.search.toLowerCase().trim();
        this.filteredAgencies = transformedData.filter(agency => {
          const nameMatch = agency.name?.toLowerCase().includes(searchLower) || false;
          const addressMatch = agency.adresse?.toLowerCase().includes(searchLower) || false;
          const emailMatch = agency.email?.toLowerCase().includes(searchLower) || false;
          const phoneMatch = agency.phone_number?.toString().includes(searchLower) || false;
          return nameMatch || addressMatch || emailMatch || phoneMatch;
        });
      } else {
        this.filteredAgencies = transformedData;
      }
      
      this.totalItems = this.filteredAgencies.length;
      
      // Ensure page is valid
      const maxPage = Math.max(1, Math.ceil(this.totalItems / this.itemsPerPage));
      if (this.appState.page > maxPage && maxPage > 0) {
        this.appState.page = maxPage;
        this.updateUrlState();
      }
      
      console.log('Display updated from cache:', {
        ville: this.appState.ville,
        page: this.appState.page,
        totalItems: this.totalItems,
        filteredCount: this.filteredAgencies.length
      });
      
      this.cdr.detectChanges();
      
    } catch (error) {
      console.error('Error updating display:', error);
      this.filteredAgencies = [];
      this.totalItems = 0;
      this.cdr.detectChanges();
    }
  }

  /**
   * Get ville data from cache or filter
   */
  private getVilleData(villeId: string): any[] {
    if (villeId === 'all') {
      return this.appState.allData;
    }
    
    // Check if we have cached ville data
    if (this.appState.villeData.has(villeId)) {
      return this.appState.villeData.get(villeId)!;
    }
    
    // Filter from all data
    const ville = this.villes.find(v => v.id === villeId);
    if (!ville || this.appState.allData.length === 0) return [];
    
    const villeValuesLower = ville.values.map(v => v.toLowerCase());
    const filteredData = this.appState.allData.filter(item => {
      const region = (item.region || '').toLowerCase();
      return villeValuesLower.some(villeValue => region.includes(villeValue) || region === villeValue);
    });
    
    // Cache it (in memory only, NO LOCALSTORAGE)
    this.appState.villeData.set(villeId, filteredData);
    
    return filteredData;
  }

  /**
   * Handle route return - when user comes back to this page
   */
  private handleRouteReturn(): void {
    console.log('Route return detected');
    
    // Update URL state if needed
    this.updateUrlState();
    
    // Immediately update display from cache
    this.updateDisplayFromCache();
  }

  /**
   * Extract data from API response
   */
  private extractDataFromResponse(response: any): any[] {
    if (!response) return [];
    
    if (Array.isArray(response)) return response;
    if (response.data && Array.isArray(response.data)) return response.data;
    
    if (typeof response === 'object') {
      for (const key in response) {
        if (Array.isArray(response[key]) && response[key].length > 0) {
          return response[key];
        }
      }
    }
    
    return [];
  }

  /**
   * Transform data for display
   */
  private transformData(data: any[]): any[] {
    return data.map(item => ({
      id: item.id || 0,
      name: item.name || 'Sans nom',
      description: item.description || '',
      adresse: item.adresse || '',
      region: (item.region || '').toLowerCase(),
      role: item.role || 'Promoteur',
      phone_number: item.phone_number || '',
      phone_number2: item.phone_number2 || '',
      phone_number_commercial: item.phone_number_commercial || '',
      email: item.email || '',
      logo_url: item.logo_url || '',
      cover_url: item.cover_url || '',
      website: item.website || '',
      facebook: item.facebook || '',
      instagram: item.instagram || '',
      linkedin: item.linkedin || '',
      google_map_url: item.google_map_url || '',
      createdAt: item.createdAt || '',
      updatedAt: item.updatedAt || '',
      publishedAt: item.publishedAt || '',
      projets: Array.isArray(item.projets) ? item.projets : [],
      newsletter_checked: item.newsletter_checked || false,
      newsletter_message: item.newsletter_message || '',
      google_business_url: item.google_business_url || '',
      chaine_youtube: item.chaine_youtube || '',
      accepte_publication: item.accepte_publication || false,
      public_contact_phone: item.public_contact_phone || '',
      public_contact_email: item.public_contact_email || '',
      publicShareMessage: item.publicShareMessage || ''
    }));
  }

  /**
   * Update URL with current state
   */
  private updateUrlState(): void {
    const queryParams: any = {};
    
    if (this.appState.page > 1) {
      queryParams['page'] = this.appState.page;
    }
    if (this.appState.ville !== 'all') {
      queryParams['ville'] = this.appState.ville;
    }
    if (this.appState.search) {
      queryParams['search'] = this.appState.search;
    }

    const url = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: queryParams
    }).toString();

    this.location.replaceState(url);
  }

  /**
   * Load agencies by specific ville
   */
  loadAgenciesByVille(villeId: string): void {
    this.appState.ville = villeId;
    this.appState.page = 1; // Reset to first page when changing ville
    this.appState.search = ''; // Clear search when changing ville
    
    // Update display immediately from cache
    this.updateDisplayFromCache();
    
    // Update URL
    this.updateUrlState();
    
    console.log('Ville changed to:', villeId);
  }

  /**
   * Get count for a specific ville
   */
  getVilleCount(villeId: string): number {
    if (villeId === 'all') return this.appState.allData.length;
    
    const villeData = this.getVilleData(villeId);
    return villeData.length;
  }

  /**
   * Get total projects count
   */
  getTotalProjects(): number {
    return this.appState.allData.reduce((total, agency) => {
      return total + (agency.projets?.length || 0);
    }, 0);
  }

  /**
   * Search input handler
   */
  onSearchInput(): void {
    this.searchSubject.next(this.appState.search);
  }

  /**
   * Perform search
   */
  performSearch(): void {
    this.appState.page = 1; // Reset to first page when searching
    this.updateDisplayFromCache();
    this.updateUrlState();
  }

  /**
   * Clear all filters
   */
  clearAllFilters(): void {
    this.appState.ville = 'all';
    this.appState.search = '';
    this.appState.page = 1;
    
    this.updateDisplayFromCache();
    this.updateUrlState();
  }

  /**
   * Get ville label from region or id
   */
  getVilleLabel(regionOrId: string): string {
    if (!regionOrId) return 'Non spécifié';
    
    // Check if it's a ville id
    const villeById = this.villes.find(v => v.id === regionOrId);
    if (villeById) return villeById.label;
    
    // Check if it's a region
    const villeByRegion = this.villes.find(v => 
      v.values.some(value => {
        const valueLower = value.toLowerCase();
        const regionLower = regionOrId.toLowerCase();
        return regionLower.includes(valueLower) || regionLower === valueLower;
      })
    );
    
    return villeByRegion ? villeByRegion.label : 
      regionOrId.charAt(0).toUpperCase() + regionOrId.slice(1);
  }

  /**
   * Get pagination pages array
   */
  getPaginationPages(): number[] {
    const totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
    const pages: number[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(1, this.currentPage - 2);
      let end = Math.min(totalPages, start + maxVisiblePages - 1);
      
      if (end - start + 1 < maxVisiblePages) {
        start = end - maxVisiblePages + 1;
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  }

  /**
   * Handle page change
   */
  onPageChange(page: number): void {
    this.appState.page = page;
    this.updateUrlState();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /**
   * Handle page size change
   */
  onPageSizeChange(size: number): void {
    this.itemsPerPage = size;
    this.appState.page = 1;
    this.updateDisplayFromCache();
    this.updateUrlState();
  }

  /**
   * Get display range for pagination info
   */
  getDisplayRange(): { start: number, end: number } {
    const start = (this.currentPage - 1) * this.itemsPerPage + 1;
    const end = Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    return { start, end };
  }

  /**
   * Handle image error
   */
  handleImageError(event: any): void {
    event.target.src = 'assets/default-logo.png';
  }

  /**
   * Delete agency with confirmation
   */
  deleteAgency(agency: any): void {
    if (!agency.id || !agency.name) return;
    
    Swal.fire({
      title: `Supprimer ${agency.name}?`,
      text: "Cette action est irréversible et supprimera également tous les projets associés.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ff4757",
      cancelButtonColor: "#2ed573",
      confirmButtonText: "Oui, supprimer",
      cancelButtonText: "Annuler",
      reverseButtons: true,
      backdrop: true,
      allowOutsideClick: false
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Suppression en cours...',
          text: 'Veuillez patienter',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });
        
        this.agenciesService.deleteItem(agency.id).subscribe({
          next: () => {
            Swal.fire({
              title: "Supprimé!",
              text: "Le promoteur a été supprimé avec succès.",
              icon: "success",
              timer: 2000,
              showConfirmButton: false
            });
            
            // Remove from cache (memory only)
            this.removeFromCache(agency.id);
            
            // Update display
            this.updateDisplayFromCache();
            this.cdr.detectChanges();
          },
          error: (error) => {
            console.error('Error deleting agency:', error);
            Swal.fire({
              title: "Erreur!",
              text: "Impossible de supprimer le promoteur. Veuillez réessayer.",
              icon: "error"
            });
          }
        });
      }
    });
  }

  /**
   * Remove agency from cache (memory only)
   */
  private removeFromCache(agencyId: number): void {
    // Remove from allData (memory cache)
    this.appState.allData = this.appState.allData.filter(item => item.id !== agencyId);
    
    // Remove from all ville caches (memory cache)
    this.appState.villeData.forEach((data, villeId) => {
      this.appState.villeData.set(villeId, data.filter(item => item.id !== agencyId));
    });
  }

  /**
   * Refresh data
   */
  refreshData(): void {
    this.isRefreshing = true;
    this.errorMessage = '';
    
    // Clear cache and reload from API
    this.appState.allData = [];
    this.appState.villeData.clear();
    this.appState.page = 1;
    this.appState.isInitialLoadComplete = false;
    this.appState.isLoading = true;
    
    this.agenciesService.getListPromoteur().subscribe({
      next: (response: any) => {
        const processedData = this.extractDataFromResponse(response);
        
        this.appState.allData = processedData;
        this.appState.isInitialLoadComplete = true;
        this.appState.isLoading = false;
        
        this.updateDisplayFromCache();
        this.isRefreshing = false;
        this.cdr.detectChanges();
        
        console.log('Data refreshed from API:', processedData.length);
      },
      error: (error) => {
        console.error('Error refreshing data:', error);
        this.errorMessage = error.message || 'Erreur lors du rafraîchissement';
        this.appState.isLoading = false;
        this.isRefreshing = false;
        this.cdr.detectChanges();
      }
    });
  }
  /**
 * Get total number of pages
 */
getTotalPages(): number {
  return Math.ceil(this.totalItems / this.itemsPerPage);
}
  /**
   * Navigate to edit page
   */
  navigateToEdit(agency: any): void {
    // Update URL state before navigating
    this.updateUrlState();
    
    this.router.navigate(['/modifier-agencie', agency.id], {
      queryParams: {
        returnPage: this.appState.page,
        returnVille: this.appState.ville,
        returnSearch: this.appState.search
      }
    });
  }
}